import React, { useRef, useEffect, useState, useCallback } from 'react';
import { AppBar, Box, Tooltip, Typography, IconButton, useTheme, useMediaQuery, Badge, Popover, List, ListItem, ListItemText, ListItemIcon, Divider, Chip, Button, Menu, MenuItem, Switch } from '@mui/material';
import { Refresh, Menu as MenuIcon, NotificationsOutlined, PlayCircle, Chat, Warning, CloudOff, Thermostat, Person, Backup, Close, Check, DeleteSweep, PhotoLibrary, Storage, Language, Delete, SignalCellularAlt } from '@mui/icons-material';
import { styled, keyframes, alpha } from '@mui/material/styles';
import { ActionIconButton } from '../styles/sharedStyles';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const outlinePulse = keyframes`
  0%, 100% { border-color: rgba(0, 212, 255, 0.2); }
  50% { border-color: rgba(0, 212, 255, 0.45); }
`;

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  background: theme.palette.background.paper,
  backdropFilter: `blur(${theme.custom?.glassBlur || 20}px)`,
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(135deg, ${theme.palette.primary.main}0d, ${theme.palette.secondary.main}0d)`,
    opacity: 0.5,
  },
}));

const RefreshButton = styled(ActionIconButton)({});

const NotificationButton = styled(IconButton)(({ theme }) => ({
  color: '#ffffff',
  marginRight: theme.spacing(1),
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.1)',
  },
}));

const NotificationPanel = styled(Box)(({ theme }) => ({
  width: 380,
  maxHeight: 520,
  background: theme.palette.background.paper,
  backdropFilter: `blur(${theme.custom?.glassBlur || 20}px)`,
  borderRadius: `${theme.custom?.cardRounding || 20}px`,
  border: '1px solid rgba(255, 255, 255, 0.12)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
}));

const NotificationHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2, 2.5),
  background: 'rgba(0, 0, 0, 0.25)',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
}));

const NotificationList = styled(List)(({ theme }) => ({
  maxHeight: 400,
  overflowY: 'auto',
  padding: 0,
  '&::-webkit-scrollbar': {
    width: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '10px',
  },
}));

const NotificationItem = styled(ListItem)(({ theme, unread }) => ({
  padding: theme.spacing(2, 2.5),
  transition: 'all 0.2s ease',
  position: 'relative',
  borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
  background: unread ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
  '&:hover': {
    background: alpha(theme.palette.white?.main || '#fff', 0.03),
    transform: 'translateX(4px)',
  },
  '&::before': unread ? {
    content: '""',
    position: 'absolute',
    left: 8,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: theme.palette.primary.main,
    boxShadow: `0 0 10px ${theme.palette.primary.main}`,
  } : {},
}));

const SwipeAction = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'side' && prop !== 'active',
})(({ theme, side, active }) => ({
  position: 'absolute',
  top: 0,
  bottom: 0,
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 3),
  color: '#fff',
  zIndex: 1,
  opacity: active ? 1 : 0.3,
  transition: 'all 0.2s ease',
  ...(side === 'left' ? {
    left: 0,
    background: `linear-gradient(90deg, ${alpha(theme.palette.success.main, 0.4)}, transparent)`,
    justifyContent: 'flex-start',
  } : {
    right: 0,
    background: `linear-gradient(270deg, ${alpha(theme.palette.error.main, 0.4)}, transparent)`,
    justifyContent: 'flex-end',
  }),
}));

const SwipeableNotificationItem = ({ notif, onRead, onDelete, onClick, t, theme, getNotificationIcon, formatTimestamp }) => {
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchStartY, setTouchStartY] = useState(null);
  const [isSwiping, setIsSwiping] = useState(false);
  const threshold = 80;

  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
    setTouchStartY(e.targetTouches[0].clientY);
    setIsSwiping(false);
  };

  const handleTouchMove = (e) => {
    if (touchStart === null) return;
    const currentTouchX = e.targetTouches[0].clientX;
    const currentTouchY = e.targetTouches[0].clientY;
    const diffX = currentTouchX - touchStart;
    const diffY = currentTouchY - touchStartY;
    
    // If we haven't decided it's a swipe yet, check if it's more vertical than horizontal
    if (!isSwiping) {
      if (Math.abs(diffY) > Math.abs(diffX) && Math.abs(diffY) > 10) {
        setTouchStart(null); // It's a scroll, cancel swipe
        return;
      }
    }

    if (Math.abs(diffX) > 10) {
      setIsSwiping(true);
      // Add some resistance
      const resistance = Math.abs(diffX) > threshold ? 0.5 : 1;
      setSwipeOffset(diffX * resistance);
      
      // Prevent page scroll when swiping
      if (e.cancelable) e.preventDefault();
    }
  };

  const handleTouchEnd = () => {
    if (swipeOffset > threshold) {
      onRead(notif.id);
    } else if (swipeOffset < -threshold) {
      onDelete(notif.id);
    }
    setSwipeOffset(0);
    setTouchStart(null);
    setTouchStartY(null);
    // Use a small delay for click prevention
    setTimeout(() => setIsSwiping(false), 100);
  };

  const onItemClick = (e) => {
    if (!isSwiping) {
      onClick(notif);
    }
  };

  return (
    <Box sx={{ position: 'relative', overflow: 'hidden', width: '100%' }}>
      {swipeOffset > 0 && (
        <SwipeAction side="left" active={swipeOffset > threshold}>
          <Check sx={{ transform: `scale(${Math.min(1.5, 0.8 + swipeOffset/100)})` }} />
        </SwipeAction>
      )}
      {swipeOffset < 0 && (
        <SwipeAction side="right" active={swipeOffset < -threshold}>
          <Delete sx={{ transform: `scale(${Math.min(1.5, 0.8 + Math.abs(swipeOffset)/100)})` }} />
        </SwipeAction>
      )}
      
      <NotificationItem 
        unread={!notif.read}
        alignItems="flex-start"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={onItemClick}
        sx={{ 
          cursor: 'pointer',
          pr: 6,
          transform: `translateX(${swipeOffset}px)`,
          transition: swipeOffset === 0 ? 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
          zIndex: 2,
          background: swipeOffset !== 0 ? theme.palette.background.paper : 'inherit',
          '&:hover': {
            transform: swipeOffset === 0 ? 'translateX(4px)' : `translateX(${swipeOffset}px)`,
          }
        }}
      >
        <NotificationIcon type={notif.type}>
          {getNotificationIcon(notif.type)}
        </NotificationIcon>
        <ListItemText
          primary={
            <Typography variant="subtitle2" sx={{ 
              color: notif.read ? 'rgba(255, 255, 255, 0.7)' : '#fff', 
              fontWeight: notif.read ? 500 : 600,
              lineHeight: 1.2,
              mb: 0.5
            }}>
              {notif.title}
            </Typography>
          }
          secondary={
            <Box component="span">
              <Typography variant="body2" component="span" sx={{ 
                color: notif.read ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.7)', 
                display: 'block',
                fontSize: '0.8rem',
                mb: 0.5
              }}>
                {notif.message}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.3)', fontWeight: 500 }}>
                {formatTimestamp(notif.timestamp)}
              </Typography>
            </Box>
          }
        />
        <Tooltip title="Dismiss">
          <IconButton 
            size="small" 
            onClick={(e) => { e.stopPropagation(); onDelete(notif.id); }}
            sx={{ 
              position: 'absolute',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'rgba(255, 255, 255, 0.2)',
              '&:hover': {
                color: theme.palette.error.main,
                background: 'rgba(255, 255, 255, 0.05)'
              }
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Tooltip>
      </NotificationItem>
    </Box>
  );
};

const NotificationIcon = styled(ListItemIcon)(({ theme, type }) => {
  const colors = {
    jellyfin: '#AA5CC3',
    immich: '#00d4ff',
    chat: '#42A5F5',
    warning: '#FFA726',
    service: '#EF5350',
    backup: '#66BB6A',
    user: '#26A69A',
    oralb: '#00d4ff', // Oral-B blue
    wan_failover: '#FF9800',
  };
  return {
    color: colors[type] || '#fff',
    minWidth: 44,
    '& svg': {
      fontSize: '1.5rem',
    }
  };
});

const ConnectionStats = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(0.75, 2.5),
  borderRadius: 999,
  background: 'rgba(255, 255, 255, 0.03)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  backdropFilter: `blur(${(theme.custom?.glassBlur || 10) * 0.5}px)`,
  [theme.breakpoints.down('md')]: {
    background: 'none',
    border: 'none',
    backdropFilter: 'none',
    padding: theme.spacing(0.1, 0.5),
    gap: theme.spacing(0.5),
    flexWrap: 'nowrap',
    overflowX: 'auto',
    minWidth: 0,
  },
}));

const ConnectionStatItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  transition: 'all 0.3s ease',
  [theme.breakpoints.down('md')]: {
    display: 'inline-flex',
    alignItems: 'flex-start',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.35, 0.9),
    borderRadius: 999,
    border: '1px solid rgba(255, 255, 255, 0.15)',
    background: 'rgba(255, 255, 255, 0.025)',
    minWidth: 0,
    flexShrink: 0,
    maxWidth: 120,
  }
}));

const ConnectionIconCircle = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  borderRadius: '50%',
  background: 'rgba(255, 255, 255, 0.05)',
  border: '1px solid rgba(255, 255, 255, 0.15)',
  position: 'relative',
  transition: 'all 0.3s ease',
  '& img': {
    width: 20,
    height: 20,
    objectFit: 'contain'
  },
  [theme.breakpoints.down('md')]: {
    width: 28,
    height: 28,
    borderRadius: 8,
    background: 'rgba(255, 255, 255, 0.03)',
    '& img': {
      width: 22,
      height: 22,
    }
  }
}));

const ConnectionTextWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  lineHeight: 1,
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column-reverse',
  }
}));

const ConnectionName = styled(Typography)(({ theme }) => ({
  fontSize: '0.65rem',
  fontWeight: 700,
  color: 'rgba(255, 255, 255, 0.9)',
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
  [theme.breakpoints.down('md')]: {
    fontSize: '0.55rem',
    letterSpacing: '0.3px',
    color: 'rgba(255, 255, 255, 0.6)',
  },
  [theme.breakpoints.down('sm')]: {
    display: 'none'
  }
}));

const ConnectionValue = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 800,
  color: theme.palette.primary.main,
  textShadow: `0 0 10px ${theme.palette.primary.main}66`,
  [theme.breakpoints.down('md')]: {
    fontSize: '0.75rem',
    fontWeight: 700,
    color: '#fff',
    textShadow: 'none',
  }
}));

const STAT_ICONS = {
  immich: '/immich.png',
  jellyfin: '/jellyfin.png',
  'app users': '/watchtower.png',
};

const getStatIcon = (label) => {
  const key = label.toLowerCase();
  return STAT_ICONS[key] || '/favicon-96x96.png';
};

const formatBytesPerSecond = (bytesPerSecond) => {
  if (!bytesPerSecond || bytesPerSecond === 0) return '0 B/s';
  const k = 1024;
  const sizes = ['B/s', 'KB/s', 'MB/s', 'GB/s'];
  const i = Math.floor(Math.log(bytesPerSecond) / Math.log(k));
  return `${(bytesPerSecond / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

const StatsWrapper = styled(Box)({
  flex: 1,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '16px',
  minWidth: 0,
});

const ApiStatusContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(0.75, 2.5),
  borderRadius: 999,
  background: 'rgba(255, 255, 255, 0.03)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  backdropFilter: `blur(${(theme.custom?.glassBlur || 10) * 0.5}px)`,
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const ApiStatusItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  transition: 'all 0.3s ease',
});

const ApiIconCircle = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'online',
})(({ online }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  borderRadius: '50%',
  background: online ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
  border: `1px solid ${online ? 'rgba(76, 175, 80, 0.3)' : 'rgba(244, 67, 54, 0.3)'}`,
  position: 'relative',
  '& img': {
    width: 20,
    height: 20,
    objectFit: 'contain',
    filter: online ? 'none' : 'grayscale(100%) opacity(0.6)',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -1,
    right: -1,
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: online ? '#4CAF50' : '#F44336',
    border: '1.5px solid rgba(7, 11, 22, 1)',
    boxShadow: `0 0 6px ${online ? 'rgba(76, 175, 80, 0.6)' : 'rgba(244, 67, 54, 0.6)'}`,
  },
}));

const ApiTextWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  lineHeight: 1,
});

const ApiName = styled(Typography)({
  fontSize: '0.65rem',
  fontWeight: 700,
  color: 'rgba(255, 255, 255, 0.9)',
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
});

const ApiStatusValue = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'online',
})(({ online }) => ({
  fontSize: '0.55rem',
  fontWeight: 500,
  color: online ? '#4CAF50' : '#F44336',
  opacity: 0.8,
  letterSpacing: '0.3px',
}));

const GlassDivider = styled(Box)(({ theme }) => ({
  width: '1px',
  height: '24px',
  background: 'linear-gradient(to bottom, transparent, rgba(255, 255, 255, 0.15), transparent)',
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const AdGuardContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(0.5, 2),
  borderRadius: 999,
  background: 'rgba(255, 255, 255, 0.03)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  backdropFilter: `blur(${(theme.custom?.glassBlur || 10) * 0.5}px)`,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.06)',
    border: '1px solid rgba(255, 255, 255, 0.15)',
  },
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const AdGuardLogo = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'online',
})(({ online }) => ({
  width: 28,
  height: 28,
  borderRadius: '50%',
  background: online ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
  border: `1px solid ${online ? 'rgba(76, 175, 80, 0.3)' : 'rgba(244, 67, 54, 0.3)'}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  '& img': {
    width: 18,
    height: 18,
    objectFit: 'contain',
    filter: online ? 'none' : 'grayscale(100%) opacity(0.6)',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -1,
    right: -1,
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: online ? '#4CAF50' : '#F44336',
    border: '1.5px solid rgba(7, 11, 22, 1)',
    boxShadow: `0 0 6px ${online ? 'rgba(76, 175, 80, 0.6)' : 'rgba(244, 67, 54, 0.6)'}`,
  },
}));

const AdGuardTextWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  lineHeight: 1,
});

const AdGuardName = styled(Typography)({
  fontSize: '0.65rem',
  fontWeight: 700,
  color: 'rgba(255, 255, 255, 0.9)',
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
});

const AdGuardStatValue = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 800,
  color: theme.palette.primary.main,
  textShadow: `0 0 10px ${theme.palette.primary.main}66`,
}));

const AdGuardToggleWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const AdGuardToggleLabel = styled(Typography)({
  fontSize: '0.6rem',
  fontWeight: 600,
  color: 'rgba(255, 255, 255, 0.6)',
  textTransform: 'uppercase',
  letterSpacing: '0.3px',
});

const AdGuardQueryPanel = styled(Box)(({ theme }) => ({
  width: 420,
  maxHeight: 520,
  background: theme.palette.background.paper,
  backdropFilter: `blur(${theme.custom?.glassBlur || 20}px)`,
  borderRadius: `${theme.custom?.cardRounding || 20}px`,
  border: '1px solid rgba(255, 255, 255, 0.12)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
}));

const AdGuardQueryHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2, 2.5),
  background: 'rgba(0, 0, 0, 0.25)',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
}));

const AdGuardQueryList = styled(Box)(({ theme }) => ({
  maxHeight: 400,
  overflowY: 'auto',
  padding: 0,
  '&::-webkit-scrollbar': {
    width: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '10px',
  },
}));

const AdGuardQueryItem = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5, 2.5),
  borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
  '&:last-child': {
    borderBottom: 'none',
  },
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.03)',
  },
}));

const RpiStatusContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(0.75, 2.5),
  borderRadius: 999,
  background: 'rgba(255, 255, 255, 0.03)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  backdropFilter: `blur(${(theme.custom?.glassBlur || 10) * 0.5}px)`,
  minWidth: '420px', // Reduced width to account for smaller gaps
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const RpiStatItem = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  lineHeight: 1,
  minWidth: '60px', // Fixed width to prevent header movement
});

const RpiStatLabel = styled(Typography)({
  fontSize: '0.5rem',
  fontWeight: 700,
  color: 'rgba(255, 255, 255, 0.5)',
  textTransform: 'uppercase',
  marginBottom: '2px',
});

const RpiStatValue = styled(Typography, {
  shouldForwardProp: (prop) => !['value', 'metricType'].includes(prop),
})(({ value, metricType }) => {
  let color = '#ffffff'; // Green - normal
  if (metricType === 'cpu' || metricType === 'ram') {
    if (value > 80) color = '#ff4444'; // Red - critical
    else if (value > 60) color = '#ffae00'; // Orange - high
    else if (value > 30) color = '#ffeb3b'; // Yellow - moderate
  } else if (metricType === 'temp') {
    if (value > 55) color = '#ff4444'; // Red - critical
    else if (value > 40) color = '#ffae00'; // Orange - high
  } else if (metricType === 'network' || metricType === 'disk') {
    if (value > 10 * 1024 * 1024) color = '#ff4444'; // >10MB/s - critical
    else if (value > 5 * 1024 * 1024) color = '#ffae00'; // >5MB/s - high
    else if (value > 1 * 1024 * 1024) color = '#ffeb3b'; // >1MB/s - moderate
  }
  return {
    fontSize: '0.7rem',
    fontWeight: 800,
    color: color,
  };
});

const Header = ({ onRefresh, onLogout, loading, connectionStats = {}, apiStatus = null, rpiStatus = null, onMenuClick, token, onViewChange, onServersSubTabChange, onDebianSubTabChange, status, adguardData }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const audioRef = useRef(null);
  // Synced read/dismissed IDs from backend (so Oral-B merge uses same state across devices)
  const syncNotificationStateRef = useRef({ read_ids: [], dismissed_ids: [] });
  const { t, i18n } = useTranslation();
  
  // Language selector state
  const [langAnchorEl, setLangAnchorEl] = useState(null);

  // AdGuard local toggle state
  const [adguardProtectionEnabled, setAdguardProtectionEnabled] = useState(adguardData?.protection_enabled ?? false);

  useEffect(() => {
    setAdguardProtectionEnabled(adguardData?.protection_enabled ?? false);
  }, [adguardData?.protection_enabled]);

  const handleLangClick = (event) => {
    setLangAnchorEl(event.currentTarget);
  };

  const handleLangClose = () => {
    setLangAnchorEl(null);
  };

  const handleLangChange = async (lang) => {
    i18n.changeLanguage(lang);
    handleLangClose();
    if (token) {
      try {
        await axios.post('/api/user/preferences', { language: lang }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (err) {
        console.error('Failed to save language preference:', err);
      }
    }
  };

  // Fetch language preference on mount
  useEffect(() => {
    const fetchLang = async () => {
      if (!token) return;
      try {
        const res = await axios.get('/api/user/preferences', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data.language && res.data.language !== i18n.language) {
          i18n.changeLanguage(res.data.language);
        }
      } catch (err) {
        console.error('Failed to fetch language preference:', err);
      }
    };
    fetchLang();
  }, [token, i18n]);

  // Notification state
  const [notifications, setNotifications] = React.useState([]);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [notifLoading, setNotifLoading] = React.useState(false);
  const [pushTestLoading, setPushTestLoading] = React.useState(false);
  const [oralbAlerts, setOralbAlerts] = React.useState([]);

  // Session popover state
  const [sessionAnchorEl, setSessionAnchorEl] = useState(null);
  const [activeSessionType, setActiveSessionType] = useState(null);

  // API popover state
  const [apiAnchorEl, setApiAnchorEl] = useState(null);
  const [selectedApi, setSelectedApi] = useState(null);

  // AdGuard popover state
  const [adguardAnchorEl, setAdguardAnchorEl] = useState(null);
  const [adguardToggleLoading, setAdguardToggleLoading] = useState(false);
  const [adguardRefreshing, setAdguardRefreshing] = useState(false);

  const handleSessionClick = (event, type) => {
    if (type === 'settings') {
      onViewChange && onViewChange(type);
      return;
    }
    setSessionAnchorEl(event.currentTarget);
    setActiveSessionType(type);
  };

  const handleSessionClose = () => {
    setSessionAnchorEl(null);
    setActiveSessionType(null);
  };

  const handleApiClick = (event, api) => {
    setApiAnchorEl(event.currentTarget);
    setSelectedApi(api);
  };

  const handleApiClose = () => {
    setApiAnchorEl(null);
    setSelectedApi(null);
  };

  const handleAdguardClick = (event) => {
    setAdguardAnchorEl(event.currentTarget);
  };

  const handleAdguardClose = () => {
    setAdguardAnchorEl(null);
  };

  const handleAdguardToggle = async () => {
    if (!token || !adguardData) return;
    const newEnabled = !adguardProtectionEnabled;
    setAdguardToggleLoading(true);
    try {
      await axios.post('/api/adguard/toggle-protection', { enabled: newEnabled }, {
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      });
      setAdguardProtectionEnabled(newEnabled);
      onRefresh && onRefresh();
    } catch (err) {
      console.error('Failed to toggle AdGuard protection:', err);
    } finally {
      setAdguardToggleLoading(false);
    }
  };

  const handleAdguardRefresh = () => {
    if (!onRefresh) return;
    setAdguardRefreshing(true);
    onRefresh();
    setTimeout(() => setAdguardRefreshing(false), 1000);
  };
  
  // Register for Web Push (PWA push on mobile) once permission is granted
  const registerPushSubscription = React.useCallback(async () => {
    if (!token || !('serviceWorker' in navigator) || !('PushManager' in window)) return;
    try {
      const reg = await navigator.serviceWorker.ready;
      if (!reg.pushManager) return;
      const perm = Notification?.permission;
      if (perm !== 'granted') return;
      const keyRes = await fetch('/api/push/vapid-public-key', { headers: { Authorization: `Bearer ${token}` } });
      if (!keyRes.ok) return;
      const { publicKey: base64Key } = await keyRes.json();
      if (!base64Key) return;
      const urlBase64 = base64Key.replace(/-/g, '+').replace(/_/g, '/');
      const binary = atob(urlBase64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: bytes });
      const subJson = sub.toJSON ? sub.toJSON() : { endpoint: sub.endpoint, keys: { p256dh: sub.getKey('p256dh'), auth: sub.getKey('auth') } };
      const keys = subJson.keys || {};
      await fetch('/api/push-subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ subscription: { endpoint: subJson.endpoint, keys: { p256dh: keys.p256dh || keys.p256dh, auth: keys.auth } } }),
      });
    } catch (e) {
      console.warn('Push subscription skipped:', e?.message || e);
    }
  }, [token]);

  // Fetch notifications
  const fetchNotifications = React.useCallback(async () => {
    if (!token) return;
    setNotifLoading(true);
    try {
      const res = await fetch('/api/notifications', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        // Backend returns notifications with read/dismissed state synced per user (same on PC, mobile, tablet)
        const processedNotifs = data.notifications || [];
        const newUnreadCount = data.unread_count ?? processedNotifs.filter(n => !n.read).length;

        // Per-device "seen" IDs: play sound only for notifications we haven't seen on this device yet
        const seenIds = JSON.parse(localStorage.getItem('nasmon_seen_notifications') || '[]');
        const currentIds = processedNotifs.map(n => n.id);
        const hasNewItems = currentIds.some(id => !seenIds.includes(id));
        if (hasNewItems && newUnreadCount > 0) {
          if (audioRef.current) {
            audioRef.current.currentTime = 0;
            audioRef.current.play().catch(e => console.warn('Audio play failed:', e));
          }
        }
        const updatedSeenIds = Array.from(new Set([...seenIds, ...currentIds])).slice(-200);
        localStorage.setItem('nasmon_seen_notifications', JSON.stringify(updatedSeenIds));

        if (newUnreadCount > 0) {
          const lastTrigger = parseInt(sessionStorage.getItem('nasmon_push_trigger_at') || '0', 10);
          const now = Date.now();
          if (now - lastTrigger > 120000) {
            try {
              const first = processedNotifs.find(n => !n.read);
              const bodyText = newUnreadCount > 1
                ? `You have ${newUnreadCount} new notifications`
                : (first?.message || `You have ${newUnreadCount} new notification(s)`);
              const tr = await fetch('/api/push/trigger', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                  unread_count: newUnreadCount,
                  title: first?.title || 'WatchTower',
                  body: bodyText,
                }),
              });
              if (tr.ok) sessionStorage.setItem('nasmon_push_trigger_at', String(now));
            } catch (e) {
              console.warn('Push trigger failed:', e?.message || e);
            }
          }
        }

        syncNotificationStateRef.current = {
          read_ids: data.read_ids || [],
          dismissed_ids: data.dismissed_ids || [],
        };
        setNotifications(processedNotifs);
        setUnreadCount(newUnreadCount);
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setNotifLoading(false);
    }
  }, [token]);

  // Fetch Oral-B brush window alerts (uses synced dismissed/read from backend)
  const fetchOralbAlerts = React.useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch('/api/oralb/alerts', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const alerts = data.alerts || [];
        
        const oralbNotifs = alerts.map(alert => ({
          id: `oralb_${alert.type}_${alert.alert_level}`,
          type: 'oralb',
          title: alert.title,
          message: alert.message,
          timestamp: new Date().toISOString(),
          read: alert.alert_level === 'completed' || alert.alert_level === 'missed',
          alert_level: alert.alert_level,
          minutes_left: alert.minutes_left,
          window: alert.window,
        }));
        
        setOralbAlerts(oralbNotifs);
        
        const { read_ids: readIds, dismissed_ids: dismissedIds } = syncNotificationStateRef.current;
        const filteredOralb = oralbNotifs
          .filter(n => !dismissedIds.includes(n.id))
          .map(n => ({ ...n, read: n.read || readIds.includes(n.id) }));
        
        // Get existing notifications (excluding any old oralb ones)
        setNotifications(prev => {
          const otherNotifs = prev.filter(n => n.type !== 'oralb');
          // Prioritize warning/critical alerts at top
          const sorted = [...filteredOralb, ...otherNotifs].sort((a, b) => {
            const priority = { warning: 0, critical: 0, upcoming: 1, missed: 2, completed: 3 };
            const aPrio = priority[a.alert_level] ?? 4;
            const bPrio = priority[b.alert_level] ?? 4;
            return aPrio - bPrio;
          });
          
          // Calculate total unread count from both backend and oralb alerts
          const unreadBackend = otherNotifs.filter(n => !n.read).length;
          const unreadOralb = filteredOralb.filter(n => 
            !n.read && (n.alert_level === 'warning' || n.alert_level === 'critical' || n.alert_level === 'upcoming')
          ).length;
          setUnreadCount(unreadBackend + unreadOralb);
          
          return sorted;
        });
      }
    } catch (err) {
      console.error('Error fetching oralb alerts:', err);
    }
  }, [token]);
  
  // Clear all notifications: mark as read + dismissed on backend (syncs across devices)
  const handleClearNotifications = async () => {
    const currentIds = notifications.map(n => n.id);
    if (currentIds.length === 0) return;
    try {
      const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
      await Promise.all([
        fetch('/api/notifications/read', { method: 'POST', headers, body: JSON.stringify({ notification_ids: currentIds }) }),
        fetch('/api/notifications/dismiss', { method: 'POST', headers, body: JSON.stringify({ notification_ids: currentIds }) }),
      ]);
    } catch (e) {
      console.warn('Clear notifications API failed:', e?.message || e);
    }
    setNotifications([]);
    setUnreadCount(0);
  };

  // Mark a single notification as read (syncs across devices)
  const handleMarkAsRead = async (id) => {
    const notification = notifications.find(n => n.id === id);
    if (notification && !notification.read) {
      try {
        await fetch('/api/notifications/read', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ notification_ids: [id] }),
        });
      } catch (e) {
        console.warn('Mark read API failed:', e?.message || e);
      }
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
  };

  // Dismiss a single notification (syncs across devices)
  const handleDismissNotification = async (id, e) => {
    if (e) e.stopPropagation();
    try {
      await fetch('/api/notifications/dismiss', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ notification_ids: [id] }),
      });
    } catch (e) {
      console.warn('Dismiss API failed:', e?.message || e);
    }
    const dismissedNotif = notifications.find(n => n.id === id);
    setNotifications(prev => prev.filter(n => n.id !== id));
    setUnreadCount(prev => (dismissedNotif && !dismissedNotif.read) ? Math.max(0, prev - 1) : prev);
  };
  
  // Handle notification click - navigate to appropriate tab
  const handleNotificationNavigate = (notification) => {
    setAnchorEl(null);
    
    handleMarkAsRead(notification.id);

    if (onViewChange) {
      switch (notification.type) {
        case 'chat':
          onViewChange('chat');
          break;
        case 'jellyfin':
          onViewChange('jellyfin');
          break;
        case 'immich':
          onViewChange('immich');
          break;
        case 'backup':
          onViewChange('servers');
          break;
        case 'warning':
        case 'service':
          onViewChange('servers');
          break;
        case 'user':
          onViewChange('docker');
          break;
        case 'oralb':
          onViewChange('oralb');
          break;
        case 'wan_failover':
          onViewChange('unifi');
          break;
        default:
          onViewChange('dashboard');
      }
    }
  };
  
  // Poll for notifications every 30 seconds
  React.useEffect(() => {
    if (token) {
      fetchNotifications();
      fetchOralbAlerts();
      const interval = setInterval(() => {
        fetchNotifications();
        fetchOralbAlerts();
      }, 30000); // 30s for notifications, also fetch oralb alerts
      return () => clearInterval(interval);
    }
  }, [token, fetchNotifications, fetchOralbAlerts]);

  // Register for push on load when permission already granted (e.g. returning user on PWA)
  React.useEffect(() => {
    if (token && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      registerPushSubscription();
    }
  }, [token, registerPushSubscription]);

  const handleNotificationClick = async (event) => {
    setAnchorEl(event.currentTarget);
    if (!token || !('Notification' in window)) return;
    if (Notification.permission === 'default') {
      const perm = await Notification.requestPermission();
      if (perm === 'granted') await registerPushSubscription();
    } else if (Notification.permission === 'granted') {
      await registerPushSubscription();
    }
  };
  
  const handleCloseNotifications = () => {
    setAnchorEl(null);
  };

  const handleTestPush = async () => {
    if (!token || pushTestLoading) return;
    setPushTestLoading(true);
    try {
      const res = await fetch('/api/push/test', { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        // Success - notification should appear in system tray
        if (window._showSnackbar) window._showSnackbar(data.message || 'Test push sent. Check your system notifications.');
      } else {
        if (window._showSnackbar) window._showSnackbar(data.detail || data.message || 'Test failed', 'error');
      }
    } catch (e) {
      if (window._showSnackbar) window._showSnackbar(e?.message || 'Test failed', 'error');
    } finally {
      setPushTestLoading(false);
    }
  };
  
  const getNotificationIcon = (type) => {
    const icons = {
      jellyfin: <PlayCircle />,
      immich: <PhotoLibrary />,
      chat: <Chat />,
      warning: <Warning />,
      service: <CloudOff />,
      backup: <Backup />,
      user: <Person />,
      pool: <Storage />,
      oralb: <Box component="img" src="/oralb.png" sx={{ width: 24, height: 24 }} />,
      wan_failover: <SignalCellularAlt />,
    };
    return icons[type] || <NotificationsOutlined />;
  };
  
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    // Ensure timestamp is treated as UTC if it doesn't have a timezone suffix
    const ts = timestamp.includes('+') || timestamp.endsWith('Z') ? timestamp : `${timestamp}Z`;
    const date = new Date(ts);
    const now = new Date();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    
    // Handle future dates or clock skew as "Just now"
    if (diffSec < 60) return t('common.just_now');
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return t('common.ago', { count: diffMin });
    const diffHour = Math.floor(diffMin / 60);
    if (diffHour < 24) return `${diffHour}h ago`;
    
    // Check if yesterday
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) {
      return `${t('common.yesterday')}, ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    }
    
    return date.toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const groupNotifications = (notifs) => {
    const groups = {
      today: [],
      yesterday: [],
      earlier: []
    };
    
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    
    notifs.forEach(n => {
      const d = new Date(n.timestamp);
      const dCopy = new Date(d);
      dCopy.setHours(0, 0, 0, 0);
      
      if (dCopy.getTime() === now.getTime()) groups.today.push(n);
      else if (dCopy.getTime() === yesterday.getTime()) groups.yesterday.push(n);
      else groups.earlier.push(n);
    });
    
    return groups;
  };

  const getAdguardReasonColor = (reason) => {
    if (!reason) return { bg: 'rgba(255, 255, 255, 0.1)', text: 'rgba(255, 255, 255, 0.7)' };
    if (reason === 'NotFilteredNotFound') return { bg: 'rgba(76, 175, 80, 0.2)', text: '#4CAF50' };
    if (reason === 'Rewrite') return { bg: 'rgba(33, 150, 243, 0.2)', text: '#2196F3' };
    if (reason === 'FilteredBlackList') return { bg: 'rgba(244, 67, 54, 0.2)', text: '#F44336' };
    if (reason === 'Filtered') return { bg: 'rgba(244, 67, 54, 0.2)', text: '#F44336' };
    return { bg: 'rgba(255, 255, 255, 0.1)', text: 'rgba(255, 255, 255, 0.7)' };
  };

  const groupedNotifs = groupNotifications(notifications);
  const { 
    immich = 0, 
    jellyfin = 0, 
    appUsers = 0,
    immichSessions = [],
    jellyfinSessions = []
  } = connectionStats;
  const hasStats = typeof immich === 'number' || typeof jellyfin === 'number' || typeof appUsers === 'number';

  const SessionPanel = styled(Box)(({ theme }) => ({
    width: 300,
    maxHeight: 400,
    background: theme.palette.background.paper,
    backdropFilter: `blur(${theme.custom?.glassBlur || 20}px)`,
    borderRadius: `${theme.custom?.cardRounding || 20}px`,
    border: '1px solid rgba(255, 255, 255, 0.12)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  }));

  const SessionItem = styled(ListItem)(({ theme }) => ({
    padding: theme.spacing(1.5, 2),
    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
    '&:last-child': {
      borderBottom: 'none',
    },
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.03)',
    }
  }));
  
  const API_ICONS = [
    { key: 'immich', label: 'Immich', icon: '/immich.png' },
    { key: 'jellyfin', label: 'Jellyfin', icon: '/jellyfin.png' },
    { key: 'ha', label: 'HA', icon: '/home-assistant-alt.png' },
  ];

  return (
    <StyledAppBar position="sticky" elevation={0}>
      <Box sx={{
        px: { xs: 2, md: 4 },
        py: { xs: 1.5, md: 2 },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {isMobile && (
            <>
              <IconButton
                onClick={onMenuClick}
                sx={{ 
                  color: '#ffffff',
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  '&:hover': { background: 'rgba(255, 255, 255, 0.1)' }
                }}
              >
                <MenuIcon />
              </IconButton>

            </>
          )}
        </Box>

        <StatsWrapper>
          {rpiStatus && (
            <RpiStatusContainer>
              <Tooltip title="Local Raspberry Pi 5 Stats">
                <Box 
                  sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }}
                  onClick={() => {
                    if (onViewChange) onViewChange('servers');
                    if (onServersSubTabChange) onServersSubTabChange(1);
                    if (onDebianSubTabChange) onDebianSubTabChange(1);
                  }}
                >
                  <img src="/raspberry-pi.png" alt="Raspberry Pi" style={{ width: 24, height: 24, objectFit: 'contain' }} />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <RpiStatItem sx={{ minWidth: '45px' }}>
                        <RpiStatLabel>CPU</RpiStatLabel>
                        <RpiStatValue value={rpiStatus.cpu_usage} metricType="cpu">
                          {rpiStatus.cpu_usage}%
                        </RpiStatValue>
                      </RpiStatItem>
                      <RpiStatItem sx={{ minWidth: '45px' }}>
                        <RpiStatLabel>RAM</RpiStatLabel>
                        <RpiStatValue value={rpiStatus.memory_usage} metricType="ram">
                          {rpiStatus.memory_usage}%
                        </RpiStatValue>
                      </RpiStatItem>
                      <RpiStatItem sx={{ minWidth: '45px' }}>
                        <RpiStatLabel>TEMP</RpiStatLabel>
                        <RpiStatValue value={rpiStatus.temperature} metricType="temp">
                          {rpiStatus.temperature}°C
                        </RpiStatValue>
                      </RpiStatItem>
                    </Box>
                    <RpiStatItem sx={{ ml: 2 }}> {/* Increased margin to maintain spacing from grouped stats */}
                      <RpiStatLabel>DL</RpiStatLabel>
                      <RpiStatValue value={rpiStatus.network_rx} metricType="network">
                        {formatBytesPerSecond(rpiStatus.network_rx)}
                      </RpiStatValue>
                    </RpiStatItem>
                    <RpiStatItem>
                      <RpiStatLabel>UL</RpiStatLabel>
                      <RpiStatValue value={rpiStatus.network_tx} metricType="network">
                        {formatBytesPerSecond(rpiStatus.network_tx)}
                      </RpiStatValue>
                    </RpiStatItem>
                    <RpiStatItem>
                      <RpiStatLabel>READ</RpiStatLabel>
                      <RpiStatValue value={rpiStatus.disk_read} metricType="disk">
                        {formatBytesPerSecond(rpiStatus.disk_read)}
                      </RpiStatValue>
                    </RpiStatItem>
                    <RpiStatItem>
                      <RpiStatLabel>WRITE</RpiStatLabel>
                      <RpiStatValue value={rpiStatus.disk_write} metricType="disk">
                        {formatBytesPerSecond(rpiStatus.disk_write)}
                      </RpiStatValue>
                    </RpiStatItem>
                  </Box>
                </Box>
              </Tooltip>
            </RpiStatusContainer>
          )}

          {rpiStatus && (apiStatus || hasStats) && <GlassDivider />}

          {apiStatus && (
            <ApiStatusContainer>
              {API_ICONS.map((api) => (
                <ApiStatusItem 
                  key={api.key} 
                  onClick={(e) => handleApiClick(e, api)}
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.8, transform: 'scale(1.02)' },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <Tooltip title={`${api.label}: ${apiStatus[api.key] ? 'Online' : 'Offline'}`}>
                    <ApiIconCircle online={apiStatus[api.key]}>
                      <img src={api.icon} alt={api.label} />
                    </ApiIconCircle>
                  </Tooltip>
                  <ApiTextWrapper>
                    <ApiName>{api.label}</ApiName>
                    <ApiStatusValue online={apiStatus[api.key]}>
                      {apiStatus[api.key] ? 'ONLINE' : 'OFFLINE'}
                    </ApiStatusValue>
                  </ApiTextWrapper>
                </ApiStatusItem>
              ))}
            </ApiStatusContainer>
          )}

          {adguardData && (
            <AdGuardContainer onClick={handleAdguardClick}>
              <Tooltip title={`AdGuard: ${adguardData.online ? 'Online' : 'Offline'} • ${adguardData.num_dns_queries ?? 0} queries • ${adguardData.blocked_percentage ?? 0}% blocked`}>
                <AdGuardLogo online={adguardData.online}>
                  <img src="/adguard-home.png" alt="AdGuard" />
                </AdGuardLogo>
              </Tooltip>
              <AdGuardTextWrapper>
                <AdGuardName>AdGuard</AdGuardName>
                <AdGuardStatValue>
                  {(adguardData.num_dns_queries ?? 0).toLocaleString()} queries • {adguardData.blocked_percentage ?? 0}%
                </AdGuardStatValue>
              </AdGuardTextWrapper>
              <AdGuardToggleWrapper>
                <AdGuardToggleLabel>{adguardProtectionEnabled ? 'ON' : 'OFF'}</AdGuardToggleLabel>
                <Switch
                  size="small"
                  checked={adguardProtectionEnabled}
                  onClick={(e) => { e.stopPropagation(); handleAdguardToggle(); }}
                  disabled={adguardToggleLoading || !adguardData.online}
                  sx={{
                    width: 28,
                    height: 16,
                    padding: 0,
                    '& .MuiSwitch-switchBase': {
                      padding: 2,
                      '&.Mui-checked': {
                        transform: 'translateX(12px)',
                        color: '#4CAF50',
                        '& + .MuiSwitch-track': {
                          backgroundColor: '#4CAF50',
                        },
                      },
                    },
                    '& .MuiSwitch-track': {
                      borderRadius: 16,
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    },
                  }}
                />
              </AdGuardToggleWrapper>
            </AdGuardContainer>
          )}

          {(apiStatus || adguardData) && hasStats && <GlassDivider />}

          {hasStats && (
            <ConnectionStats>
              {[
                { label: 'Immich', value: immich, icon: 'Immich', tab: 'immich', type: 'immich' },
                { label: 'Jellyfin', value: jellyfin, icon: 'Jellyfin', tab: 'jellyfin', type: 'jellyfin' },
                { label: 'WTower', value: appUsers, icon: 'App Users', tab: 'settings', type: 'settings' }
              ].map(({ label, value, icon, tab, type }) => (
                <ConnectionStatItem 
                  key={label} 
                  onClick={(e) => handleSessionClick(e, type)}
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': {
                      opacity: 0.8,
                      transform: 'scale(1.02)',
                    }
                  }}
                >
                  <ConnectionIconCircle>
                    <img src={getStatIcon(icon)} alt={`${label} icon`} />
                  </ConnectionIconCircle>
                  <ConnectionTextWrapper>
                    <ConnectionName>{label}</ConnectionName>
                    <ConnectionValue>{value ?? 0}</ConnectionValue>
                  </ConnectionTextWrapper>
                </ConnectionStatItem>
              ))}
            </ConnectionStats>
          )}
        </StatsWrapper>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title={t('header.notifications')}>
            <NotificationButton onClick={handleNotificationClick}>
              <Badge badgeContent={unreadCount} color="error" max={99}>
                <NotificationsOutlined />
              </Badge>
            </NotificationButton>
          </Tooltip>
          
          {/* Notifications Popover */}
          <Popover
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleCloseNotifications}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            PaperProps={{
              sx: {
                mt: 1.5,
                background: 'transparent',
                boxShadow: 'none',
                overflow: 'visible',
              }
            }}
          >
            <NotificationPanel>
              <NotificationHeader>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#fff' }}>
                  {t('header.notifications')} {unreadCount > 0 && `(${unreadCount})`}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Tooltip title="Send a test notification to your system tray (Windows/Android)">
                    <Button
                      size="small"
                      onClick={handleTestPush}
                      disabled={pushTestLoading}
                      sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.7rem', textTransform: 'none', minWidth: 0, px: 1 }}
                    >
                      {pushTestLoading ? '…' : 'Test push'}
                    </Button>
                  </Tooltip>
                  {notifications.length > 0 && (
                    <Tooltip title={t('header.clear_all')}>
                      <IconButton 
                        onClick={handleClearNotifications}
                        size="small" 
                        sx={{ color: 'rgba(255, 255, 255, 0.6)' }}
                      >
                        <DeleteSweep fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </NotificationHeader>
              <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />
              <NotificationList>
                {notifications.length === 0 ? (
                  <Box sx={{ py: 6, textAlign: 'center' }}>
                    <NotificationsOutlined sx={{ fontSize: '3rem', color: 'rgba(255, 255, 255, 0.1)', mb: 2 }} />
                    <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontWeight: 500 }}>
                      {t('header.no_notifications')}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.3)' }}>
                      We'll notify you when something happens
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {Object.entries(groupedNotifs).map(([key, group]) => (
                      group.length > 0 && (
                        <React.Fragment key={key}>
                          <Box sx={{ 
                            px: 2.5, 
                            py: 1, 
                            background: 'rgba(255, 255, 255, 0.02)', 
                            borderBottom: '1px solid rgba(255, 255, 255, 0.05)' 
                          }}>
                            <Typography variant="caption" sx={{ 
                              color: 'rgba(255, 255, 255, 0.4)', 
                              fontWeight: 700, 
                              textTransform: 'uppercase',
                              letterSpacing: '1px'
                            }}>
                              {t(`common.${key}`)}
                            </Typography>
                          </Box>
                          {group.map((notif) => (
                            <SwipeableNotificationItem 
                              key={notif.id} 
                              notif={notif}
                              onRead={handleMarkAsRead}
                              onDelete={handleDismissNotification}
                              onClick={handleNotificationNavigate}
                              t={t}
                              theme={theme}
                              getNotificationIcon={getNotificationIcon}
                              formatTimestamp={formatTimestamp}
                            />
                          ))}
                        </React.Fragment>
                      )
                    ))}
                  </>
                )}
              </NotificationList>
              {notifications.length > 10 && (
                <Box sx={{ p: 1.5, textAlign: 'center', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                    Showing 10 of {notifications.length} notifications
                  </Typography>
                </Box>
              )}
            </NotificationPanel>
          </Popover>
          
          <Tooltip title={t('header.language')}>
            <IconButton
              onClick={handleLangClick}
              size="medium"
              sx={{ 
                color: '#ffffff',
                ml: 1,
                background: 'rgba(255, 255, 255, 0.05)',
                '&:hover': { background: 'rgba(255, 255, 255, 0.1)' }
              }}
            >
              <Language sx={{ fontSize: '1.25rem' }} />
              <Typography sx={{ ml: 0.5, fontSize: '0.8rem', fontWeight: 600 }}>
                {i18n.language === 'pl' ? 'PL' : 'EN'}
              </Typography>
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={langAnchorEl}
            open={Boolean(langAnchorEl)}
            onClose={handleLangClose}
            PaperProps={{
              sx: {
                mt: 1.5,
                background: 'rgba(7, 11, 22, 0.9)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                color: '#fff',
                '& .MuiMenuItem-root': {
                  gap: 1.5,
                  py: 1,
                  px: 2,
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.05)',
                  },
                },
              }
            }}
          >
            <MenuItem onClick={() => handleLangChange('en')}>
              <span style={{ fontSize: '1.2rem' }}>🇺🇸</span>
              <Typography variant="body2">{t('header.english')}</Typography>
              {i18n.language === 'en' && <Check sx={{ ml: 'auto', fontSize: '1rem', color: theme.palette.primary.main }} />}
            </MenuItem>
            <MenuItem onClick={() => handleLangChange('pl')}>
              <span style={{ fontSize: '1.2rem' }}>🇵🇱</span>
              <Typography variant="body2">{t('header.polish')}</Typography>
              {i18n.language === 'pl' && <Check sx={{ ml: 'auto', fontSize: '1rem', color: theme.palette.primary.main }} />}
            </MenuItem>
          </Menu>
        </Box>

        {/* API Status Popover */}
        <Popover
          open={Boolean(apiAnchorEl)}
          anchorEl={apiAnchorEl}
          onClose={handleApiClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          PaperProps={{
            sx: {
              mt: 1.5,
              background: 'transparent',
              boxShadow: 'none',
              overflow: 'visible',
            }
          }}
        >
          <SessionPanel sx={{ width: 280 }}>
            <Box sx={{ 
              p: 2, 
              background: 'rgba(0,0,0,0.2)', 
              borderBottom: '1px solid rgba(255,255,255,0.1)',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5
            }}>
              {selectedApi && (
                <img 
                  src={selectedApi.icon} 
                  alt="icon" 
                  style={{ width: 20, height: 20 }} 
                />
              )}
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#fff', textTransform: 'uppercase' }}>
                {selectedApi?.label} Status
              </Typography>
            </Box>
            <Box sx={{ p: 2 }}>
              {(() => {
                if (!selectedApi || !status) return null;
                
                let detail = null;
                if (selectedApi.key === 'truenas') {
                  detail = {
                    is_online: status.truenas?.is_online,
                    response_time: status.truenas?.response_time,
                    last_checked: status.truenas?.last_seen
                  };
                } else {
                  const sName = selectedApi.key === 'ha' ? 'home_assistant' : selectedApi.key;
                  const service = status.services?.find(s => s.name.toLowerCase() === sName.toLowerCase());
                  if (service) {
                    detail = {
                      is_online: service.is_online,
                      response_time: service.response_time,
                      last_checked: service.last_checked
                    };
                  }
                }

                if (!detail) return <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center' }}>Status unavailable</Typography>;

                return (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>Status</Typography>
                      <Chip 
                        size="small"
                        label={detail.is_online ? 'ONLINE' : 'OFFLINE'} 
                        color={detail.is_online ? 'success' : 'error'}
                        sx={{ fontWeight: 700, height: 20, fontSize: '0.65rem' }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>Response Time</Typography>
                      <Typography variant="body2" sx={{ color: detail.is_online ? '#4CAF50' : 'rgba(255,255,255,0.3)', fontWeight: 600 }}>
                        {detail.response_time ? `${Math.round(detail.response_time)}ms` : '--'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>Last Checked</Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                        {detail.last_checked ? formatTimestamp(detail.last_checked) : 'Never'}
                      </Typography>
                    </Box>
                  </Box>
                );
              })()}
            </Box>
            <Box sx={{ p: 1, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
              <Button 
                fullWidth 
                size="small" 
                onClick={() => {
                  onViewChange && onViewChange('servers');
                  handleApiClose();
                }}
                sx={{ 
                  color: theme.palette.primary.main,
                  fontSize: '0.7rem',
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                View Services Monitoring
              </Button>
            </Box>
          </SessionPanel>
        </Popover>

        <Popover
          open={Boolean(sessionAnchorEl)}
          anchorEl={sessionAnchorEl}
          onClose={handleSessionClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          PaperProps={{
            sx: {
              mt: 1.5,
              background: 'transparent',
              boxShadow: 'none',
              overflow: 'visible',
            }
          }}
        >
          <SessionPanel>
            <Box sx={{ 
              p: 2, 
              background: 'rgba(0,0,0,0.2)', 
              borderBottom: '1px solid rgba(255,255,255,0.1)',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5
            }}>
              <img 
                src={activeSessionType === 'immich' ? '/immich.png' : '/jellyfin.png'} 
                alt="icon" 
                style={{ width: 20, height: 20 }} 
              />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#fff', textTransform: 'uppercase' }}>
                {activeSessionType} Sessions
              </Typography>
            </Box>
            <List sx={{ p: 0, overflowY: 'auto' }}>
              {activeSessionType === 'immich' ? (
                immichSessions.length === 0 ? (
                  <Typography variant="body2" sx={{ p: 3, textAlign: 'center', color: 'rgba(255,255,255,0.5)' }}>
                    No active sessions
                  </Typography>
                ) : (
                  immichSessions.map((session, idx) => (
                    <SessionItem key={idx}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <Person sx={{ color: '#00d4ff', fontSize: '1.2rem' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={<Typography variant="body2" sx={{ fontWeight: 600, color: '#fff' }}>{session.userName}</Typography>}
                        secondary={
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'block' }}>
                            {session.deviceType} • {session.deviceOS}
                          </Typography>
                        }
                      />
                    </SessionItem>
                  ))
                )
              ) : activeSessionType === 'jellyfin' ? (
                jellyfinSessions.length === 0 ? (
                  <Typography variant="body2" sx={{ p: 3, textAlign: 'center', color: 'rgba(255,255,255,0.5)' }}>
                    No active sessions
                  </Typography>
                ) : (
                  jellyfinSessions.map((session, idx) => (
                    <SessionItem key={idx}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <PlayCircle sx={{ color: '#AA5CC3', fontSize: '1.2rem' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={<Typography variant="body2" sx={{ fontWeight: 600, color: '#fff' }}>{session.userName}</Typography>}
                        secondary={
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'block' }}>
                            {session.client} • {session.deviceName}
                          </Typography>
                        }
                      />
                    </SessionItem>
                  ))
                )
              ) : null}
            </List>
            {(activeSessionType === 'immich' || activeSessionType === 'jellyfin') && (
              <Box sx={{ p: 1, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                <Button 
                  fullWidth 
                  size="small" 
                  onClick={() => {
                    onViewChange && onViewChange(activeSessionType);
                    handleSessionClose();
                  }}
                  sx={{ 
                    color: activeSessionType === 'immich' ? '#00d4ff' : '#AA5CC3',
                    fontSize: '0.7rem',
                    textTransform: 'none',
                    fontWeight: 600
                  }}
                >
                  View Details
                </Button>
              </Box>
            )}
            </SessionPanel>
          </Popover>

          {/* AdGuard Query Log Popover */}
          <Popover
            open={Boolean(adguardAnchorEl)}
            anchorEl={adguardAnchorEl}
            onClose={handleAdguardClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            PaperProps={{
              sx: {
                mt: 1.5,
                background: 'transparent',
                boxShadow: 'none',
                overflow: 'visible',
              }
            }}
          >
            <AdGuardQueryPanel>
              <AdGuardQueryHeader>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <img src="/adguard-home.png" alt="AdGuard" style={{ width: 20, height: 20, objectFit: 'contain' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#fff', textTransform: 'uppercase' }}>
                    AdGuard Query Log
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    size="small"
                    label={`${adguardData?.num_dns_queries ?? 0} total`}
                    sx={{ fontWeight: 700, height: 20, fontSize: '0.65rem', background: 'rgba(255,255,255,0.1)' }}
                  />
                  <Tooltip title="Refresh query log">
                    <IconButton 
                      size="small"
                      onClick={handleAdguardRefresh}
                      disabled={adguardRefreshing}
                      sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                    >
                      <Refresh sx={{ fontSize: '1rem' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </AdGuardQueryHeader>
              <AdGuardQueryList>
                {(adguardData?.querylog || []).length === 0 ? (
                  <Box sx={{ py: 6, textAlign: 'center' }}>
                    <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontWeight: 500 }}>
                      No recent queries
                    </Typography>
                  </Box>
                ) : (
                  (adguardData?.querylog || []).slice(0, 20).map((query, idx) => (
                    <AdGuardQueryItem key={idx}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#fff', fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {query.question?.name || query.domain || '—'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)', display: 'block', fontSize: '0.7rem' }}>
                            Client: {query.client || '—'}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                          {(() => {
                            const reasonColor = getAdguardReasonColor(query.reason || query.status);
                            return (
                              <Chip
                                size="small"
                                label={query.reason || query.status || '—'}
                                sx={{
                                  fontWeight: 700,
                                  height: 18,
                                  fontSize: '0.6rem',
                                  background: reasonColor.bg,
                                  color: reasonColor.text,
                                }}
                              />
                            );
                          })()}
                          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.3)', display: 'block', fontSize: '0.65rem', mt: 0.5 }}>
                            {query.time ? new Date(query.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}
                          </Typography>
                        </Box>
                      </Box>
                    </AdGuardQueryItem>
                  ))
                )}
              </AdGuardQueryList>
            </AdGuardQueryPanel>
          </Popover>

          {/* Notification sound - professional ping */}
        <audio ref={audioRef} preload="auto">
          <source src="https://assets.mixkit.co/active_storage/sfx/2358/2358-preview.mp3" type="audio/mpeg" />
        </audio>
      </Box>
    </StyledAppBar>
  );
};

export default Header;
